'use client';

import { useState } from 'react';
import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';
import { allowedFilterParams } from '@api/allowedParams';
import { paginationPages } from '@/src/utils/paginationPages';
import Grid from '@components/Grid';
import Pagination from '@components/Pagination';
import Filter from '@components/Filter/Filter';
import ActiveFilters from '@components/Filter/ActiveFilters';
import { useLocale, useTranslations } from 'next-intl';
import { useLocaleRoutes } from '@/src/utils/useLocaleRoutes';
import { useRouter } from 'next/navigation';
import getMainCategories, {
  Category,
  getCategoryBySlug,
  getParentCategory,
} from '@/src/utils/getMainCategories';
import Sorting from '@components/Sorting';
import getOption from '@/src/utils/getOption';
import replaceContact from '@/src/utils/replaceContact';
import SubcategoriesNavbar from '@components/SubcategoriesNavbar';
import BannerFrontBottomFixed from '@components/Banners/BannerFrontBottomFixed';
import { useGetLocalizedName } from '@/src/utils/internationalization/getLocalizedName';
import Breadcrumbs from '@components/Breadcrumbs';
import { useCategoriesData } from '@/src/redux/categoriesDataContext';

export default function ClientPage({
  slug,
  data,
  subCat = false,
}: {
  slug: string;
  data: any;
  subCat?: any;
}) {
  const t = useTranslations('page.category');
  const g = useTranslations('general');
  const path = useLocaleRoutes();
  const pathname = usePathname();
  const { push } = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const getLocalizedName = useGetLocalizedName();
  const categoriesData = useCategoriesData();
  const q = (param: string): string => {
    return searchParams.get(param) ? String(searchParams.get(param)) : '';
  };
  const [sorting, setSorting] = useState(q('sort') ? q('sort') : '-published');
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const defaultSorting = !q('sort');
  const categories = getMainCategories();

  let category = null as null | Category;
  categories.forEach((cat) => {
    if (slug === cat.slug) {
      category = cat;
    }
  });
  if (!category && slug !== 'all') {
    push(`/${path('all')}`);
  }
  const categoryId = category?.id;
  const catData = getCategoryBySlug(String(category?.slug));
  const subCatTitle = subCat ? getLocalizedName(subCat) : false;

  const categoriesTree = [];
  let parentCategory = subCat?.id || categoryId;
  while (parentCategory) {
    categoriesTree.push(parentCategory);
    parentCategory = getParentCategory(categoriesData, parentCategory);
    if (!parentCategory) {
      break;
    }
  }

  let breadcrumbsList = [] as any[];
  let breadcrumbPath = '';
  let description = '';
  categoriesTree.reverse().forEach((categoryId) => {
    const category = categoriesData.find(
      (category: any) => category.id === categoryId
    );
    if (category) {
      breadcrumbPath += '/' + category.translations[0].translated_slug;
      breadcrumbsList.push({
        label: getLocalizedName(category),
        path: breadcrumbPath,
      });
      description = category.description || '';
    }
  });

  let activeFilters = false;
  let filterParams = {} as any;
  if (categoryId) {
    filterParams['category_id'] = categoryId;
  }
  allowedFilterParams.forEach((param) => {
    if (searchParams.get(param)) {
      filterParams[param] = searchParams.get(param);
      activeFilters = true;
    }
  });

  const currentQuery = new URLSearchParams(Array.from(searchParams.entries()));
  const currentPage = parseInt(q('page')) > 0 ? parseInt(q('page')) : 1;

  let pages = [] as number[];
  if (data?.hasOwnProperty('last_page')) {
    pages = paginationPages(data.current_page, data.last_page);
  }

  const updateSortingQuery = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSorting(e.currentTarget.value);
    const currentQuery = new URLSearchParams(
      Array.from(searchParams.entries())
    );
    currentQuery.set('sort', e.currentTarget.value);
    currentQuery.delete('page');
    push(`${pathname}?${currentQuery.toString()}`);
  };

  const customTitle = catData['title-' + locale];
  const title = getLocalizedName(catData);

  const descriptionHtml = () => {
    if (!description) {
      return null;
    }
    return (
      <div className='category-description prose mb-4 max-w-[41em]'>
        {description.length < 300 ? (
          <div dangerouslySetInnerHTML={{ __html: description }} />
        ) : (
          <>
            <div
              className={`${descriptionExpanded ? 'grid-rows-[1fr] ease-[cubic-bezier(0,1,0,1)]' : 'grid-rows-[0fr] ease-[cubic-bezier(.49,0,0,1)]'} !duration-900 grid transition-[grid-template-rows]`}
              style={{ transitionDuration: '.9s' }}
            >
              <div
                dangerouslySetInnerHTML={{ __html: description }}
                className='min-h-[4.7em] overflow-hidden'
              />
            </div>
            <button
              onClick={() => setDescriptionExpanded(!descriptionExpanded)}
              className='mt-2 font-bold uppercase text-brand'
            >
              {descriptionExpanded ? g('readLess') : g('readMore')}
              <Image
                src='/images/arrow.svg'
                alt=''
                width={16}
                height={16}
                className={`ml-2 inline-block transform ${
                  descriptionExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className='mt-4 flex flex-col gap-y-6'>
        {!!breadcrumbsList.length && (
          <section className='mt-2'>
            <Breadcrumbs items={breadcrumbsList} />
          </section>
        )}
        <div className='flex flex-wrap items-end justify-between gap-4 border-b-4 border-brand pb-4'>
          <h1 className='w-full border-b-2 border-brand pb-2 text-xl uppercase md:text-3xl'>
            {customTitle}
            {customTitle && subCatTitle && ` - ${subCatTitle}`}
            {!customTitle && (
              <>
                {slug === 'all' && t('headerAll')}
                {slug !== 'all' &&
                  (subCatTitle
                    ? t('headerWithSub', {
                        category: title,
                        subCategory: subCatTitle,
                      })
                    : t('header', {
                        category: title,
                      }))}
              </>
            )}
          </h1>
          {descriptionHtml()}
          {!subCat && <SubcategoriesNavbar />}
          <div className='relative ml-auto grid w-full grid-cols-2 items-center gap-4 sm:gap-y-10 md:flex md:w-auto md:gap-4 lg:grid lg:grid-cols-3'>
            <div className='col-span-1 hidden lg:block'></div>
            <Sorting
              sorting={sorting}
              onChange={(e) => updateSortingQuery(e)}
              defaultSorting={defaultSorting}
            />
            <Filter slug={slug} category={category} subCat={subCat} />
          </div>
        </div>
        {activeFilters && <ActiveFilters />}
      </div>
      {!!data && !!data?.total ? (
        <div className='my-4 text-sm'>
          {t('showingResults', {
            from: data.from,
            to: data.to,
            total: data.total,
          })}
        </div>
      ) : (
        /* empty div for flexbox reasons */
        <div />
        /** **/
      )}
      {!!data?.data?.length && <Grid items={data.data} />}
      {!data?.data?.length && (
        <div className='relative mt-6 flex flex-col items-center items-center justify-center p-10 lg:min-h-[40vh]'>
          <Image
            src='/images/landscape.jpg'
            alt=''
            fill
            style={{ objectFit: 'cover', objectPosition: 'center' }}
            className='absolute z-0'
          />
          <div className='relative z-10 mb-6 flex max-w-[600px] flex-wrap items-center justify-center gap-12 lg:flex-nowrap'>
            <Image
              src='/images/sad-search.svg'
              alt=''
              width={150}
              height={150}
              className='h-auto w-[120px]'
            />
            <div>
              <h2 className='mb-2 text-2xl font-semibold md:text-4xl'>
                {t('noResults')}
              </h2>
              {getOption('SIBLING_SITE_URL') && (
                <span
                  dangerouslySetInnerHTML={{
                    __html: replaceContact(t('noResultsLink')),
                  }}
                ></span>
              )}
            </div>
          </div>
        </div>
      )}
      {pages.length > 1 && (
        <Pagination
          pages={pages}
          currentPage={currentPage}
          query={currentQuery}
        />
      )}
      {!getOption('HAS_AUCTIONS') && !!getOption('SIBLING_SITE_URL') && (
        <>
          <div className='mt-12' />
          <BannerFrontBottomFixed />
        </>
      )}
    </>
  );
}
